import { flash, SubmitButton } from "@xola/ui-kit";
import { FormField, SwitchField } from "common";
import React from "react";
import { useMutation } from "react-query";
import { AutoLogoutPreference } from "types";
import { number, object } from "yup";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { TimePickerPopover } from "@/components/Common/TimePickerPopover";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";

const AUTO_LOGOUT_MIN_MINUTES = 10;
const AUTO_LOGOUT_MAX_MINUTES = 20_160; // 14 days, the token lifetime
const AUTO_LOGOUT_DEFAULT_MINUTES = AUTO_LOGOUT_MAX_MINUTES;

const validationSchema = object().shape({
    idleMinutes: number()
        .integer()
        .min(AUTO_LOGOUT_MIN_MINUTES, "Minimum is 10 minutes")
        .max(AUTO_LOGOUT_MAX_MINUTES, "Maximum is 14 days"),
});

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

export const SessionTimeoutSetting = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const initialValues: AutoLogoutPreference = {
        enabled: seller.preferences?.autoLogout?.enabled ?? false,
        idleMinutes: seller.preferences?.autoLogout?.idleMinutes ?? AUTO_LOGOUT_DEFAULT_MINUTES,
    };

    const { mutateAsync: savePreference, isLoading } = useMutation(async (payload: AutoLogoutPreference) => {
        try {
            await api.post(`/users/${seller.id}/preferences/autoLogout`, payload);
            invalidate("sellers", seller.id);
            flash.success("Session timeout updated");
        } catch {
            flash.danger("Error updating session timeout");
        }
    });

    const handleSubmit = async (values: AutoLogoutPreference) => savePreference(values);

    return (
        <XolaFormik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit: submitForm, resetForm, setFieldValue, values, dirty }) => (
                <div data-testid="session-timeout-setting">
                    <FormField name="idleMinutes" label="Session Timeout">
                        <SwitchField
                            isChecked={values.enabled}
                            onChange={(isEnabled) => setFieldValue("enabled", isEnabled)}
                        >
                            Automatically log out users from Xola Web App after{" "}
                            {values.enabled ? (
                                <>
                                    <span onClick={stopPropagation}>
                                        <TimePickerPopover
                                            submitOnApply={false}
                                            value={values.idleMinutes}
                                            onApply={(minutes) => setFieldValue("idleMinutes", minutes)}
                                        />
                                    </span>
                                    <span className="block">of idle time</span>
                                </>
                            ) : (
                                <>
                                    a period <span className="block">of inactivity</span>
                                </>
                            )}
                        </SwitchField>
                    </FormField>
                    <div className="flex gap-x-3 pt-2">
                        <SubmitButton isLoading={isLoading} disabled={!dirty} onClick={submitForm}>
                            Save
                        </SubmitButton>
                        <SecondaryButton disabled={!dirty} onClick={() => resetForm()}>
                            Cancel
                        </SecondaryButton>
                    </div>
                </div>
            )}
        </XolaFormik>
    );
};
